import * as React from 'react';
import { ImagePlus, Trash2 } from 'lucide-react';
import { Button, FilePicker } from '@shigo-ui/ui';

import { useObjectUrl } from '../../../../../hooks/useObjectUrl';
import { dashboardT } from '../../../../../lib/messages';

type BrandLogoFieldProps = {
  existingLogoUrl?: string | null;
  file: File | null | undefined;
  removeExistingLogo: boolean;
  disabled: boolean;
  onFileChange: (file: File | null) => void;
  onRemoveExistingLogo: () => void;
};

export function BrandLogoField({
  existingLogoUrl,
  file,
  removeExistingLogo,
  disabled,
  onFileChange,
  onRemoveExistingLogo,
}: BrandLogoFieldProps) {
  const [imageFailed, setImageFailed] = React.useState(false);
  const filePreviewUrl = useObjectUrl(file);
  const hasExistingLogo = Boolean(existingLogoUrl && !removeExistingLogo);
  const previewUrl = resolvePreviewUrl({
    file,
    filePreviewUrl,
    existingLogoUrl,
    removeExistingLogo,
  });

  function handleFileChange(nextFile: File | null) {
    setImageFailed(false);
    onFileChange(nextFile);
  }

  return (
    <div className="border-input bg-card grid gap-4 rounded-md border p-4 shadow-[var(--shadow-control)] sm:grid-cols-[120px_minmax(0,1fr)] sm:items-center">
      <div className="border-input bg-background flex aspect-[3/2] items-center justify-center overflow-hidden rounded-md border p-3 shadow-[var(--shadow-control)]">
        {previewUrl && !imageFailed ? (
          <img
            src={previewUrl}
            alt={dashboardT('brands.form.logoPreview')}
            className="h-full w-full object-contain"
            onError={() => {
              setImageFailed(true);
            }}
          />
        ) : (
          <ImagePlus className="text-muted-foreground h-6 w-6" aria-hidden="true" />
        )}
      </div>
      <div className="grid min-w-0 gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
        <FilePicker
          label={dashboardT('brands.logo')}
          value={file ?? null}
          onFileChange={handleFileChange}
          accept="image/*"
          buttonLabel={
            hasExistingLogo
              ? dashboardT('brands.form.changeFile')
              : dashboardT('brands.form.chooseFile')
          }
          helperText={dashboardT('brands.form.logoHelper')}
          helperTextPlacement="below"
          disabled={disabled}
        />
        {hasExistingLogo ? (
          <Button
            type="button"
            variant="outline"
            className="w-fit"
            disabled={disabled}
            onClick={onRemoveExistingLogo}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            {dashboardT('brands.form.removeLogo')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function resolvePreviewUrl({
  file,
  filePreviewUrl,
  existingLogoUrl,
  removeExistingLogo,
}: {
  file: File | null | undefined;
  filePreviewUrl: string | null;
  existingLogoUrl: string | null | undefined;
  removeExistingLogo: boolean;
}) {
  if (file) {
    return filePreviewUrl;
  }
  if (removeExistingLogo) {
    return null;
  }
  return existingLogoUrl ?? null;
}
